import React, { memo, useState } from 'react';
import {
  Star,
  PackageCheck,
  FileText,
  MessageSquare,
  Info
} from 'lucide-react';
import { Arquivo } from '../../types/gerArquivos';
import { ArquivoDetalhesModal } from './ArquivoDetalhesModal';

// Mapeamento de categorias
const CATEGORIAS = {
  1: 'DTF TÊXTIL',
  2: 'DTF UV',
  3: 'SUBLIMÁTICA',
  4: 'CAMISETA'
} as const;

function getCategoriaColor(categoria: number): string {
  switch (categoria) {
    case 1: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 2: return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 3: return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 4: return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  }
}

interface ListaArquivosProps {
  arquivos: Arquivo[];
  carregando: boolean;
  onToggleFavorito: (arquivo: Arquivo) => void;
  onAbrirArquivo: (arquivo: Arquivo) => void;
  onAbrirDiretorio: (arquivo: Arquivo) => void;
  temFiltrosAtivos?: boolean;
}

function ListaArquivosComponent({
  arquivos,
  carregando,
  onToggleFavorito,
  onAbrirArquivo,
  onAbrirDiretorio,
  temFiltrosAtivos = false
}: ListaArquivosProps) {
  const [arquivoDetalhes, setArquivoDetalhes] = useState<Arquivo | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'salvo':
        return 'text-green-400 bg-green-400/10';
      case 'em_edicao':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'finalizado':
        return 'text-blue-400 bg-blue-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'salvo':
        return 'Salvo';
      case 'em_edicao':
        return 'Em edição';
      case 'finalizado':
        return 'Finalizado';
      default:
        return 'Desconhecido';
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
          <span>Carregando arquivos...</span>
        </div>
      </div>
    );
  }

  if (arquivos.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum arquivo encontrado</p>
        {temFiltrosAtivos && (
          <p className="text-sm mt-2">Tente ajustar os filtros de busca</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Desktop Table View (>= 640px) - REDESIGN MINIMALISTA */}
        <div className="hidden sm:block bg-navy-800 rounded-lg overflow-hidden">
          {/* Header da tabela - 4 colunas */}
          <div className="grid grid-cols-10 gap-3 p-3 bg-navy-700 text-xs font-medium text-gray-300 border-b border-navy-600">
            <div className="col-span-4">Arquivo</div>
            <div className="col-span-3">Cliente</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>

          {/* Linhas da tabela */}
          <div className="divide-y divide-navy-600">
            {arquivos.map((arquivo) => {
              const formattedDate = new Date(arquivo.dataCriacao).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });

              return (
              <div
                key={arquivo.id}
                className="grid grid-cols-10 gap-3 p-3 hover:bg-navy-700/50 transition-colors group"
                title={`Criado em: ${formattedDate}`}
              >
                {/* Coluna Arquivo + Categoria (40%) */}
                <div className="col-span-4 flex flex-col gap-1.5 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                    <span
                      className="text-sm text-white font-medium truncate"
                      title={arquivo.arquivoCaminho || arquivo.arquivoNome}
                    >
                      {arquivo.arquivoNome}
                    </span>
                    {arquivo.favorito && (
                      <Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
                    )}
                  </div>
                  {arquivo.prodCatItem && (
                    <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getCategoriaColor(arquivo.prodCatItem)}`}>
                      {CATEGORIAS[arquivo.prodCatItem]}
                    </span>
                  )}
                </div>

                {/* Coluna Cliente + WhatsApp (30%) */}
                <div className="col-span-3 flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm text-gray-200 truncate" title={arquivo.cliente?.nome || '-'}>
                    {arquivo.cliente?.nome || '-'}
                  </span>
                  {arquivo.cliWhatsapp && (
                    <span className="text-xs text-gray-500 truncate">{arquivo.cliWhatsapp}</span>
                  )}
                </div>

                {/* Coluna Status (20%) */}
                <div className="col-span-2 flex items-center gap-2">
                  {arquivo.source === 'cdr_save' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-purple-400 bg-purple-400/10" title={`O-${arquivo.orcId} / I-${arquivo.oitId}`}>
                      Orçamento
                    </span>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(arquivo.status)}`}>
                      {getStatusText(arquivo.status)}
                    </span>
                  )}
                  {/* Indicador de observações */}
                  {arquivo.observacoes && (
                    <button
                      onClick={() => setArquivoDetalhes(arquivo)}
                      className="p-1 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-all opacity-0 group-hover:opacity-100"
                      title="Ver observações e detalhes"
                      aria-label="Ver detalhes do arquivo"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Coluna Ações (10%) */}
                <div className="col-span-1 flex items-center gap-1 justify-end">
                  {/* Botão Info - sempre visível se hover */}
                  <button
                    onClick={() => setArquivoDetalhes(arquivo)}
                    className="p-1 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-all opacity-0 group-hover:opacity-100"
                    title="Ver detalhes completos"
                    aria-label="Ver detalhes"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>

                  {/* Botão Favorito (apenas pré-orçamentos) */}
                  {arquivo.source !== 'cdr_save' && (
                    <button
                      onClick={() => onToggleFavorito(arquivo)}
                      className={`p-1 rounded transition-all ${
                        arquivo.favorito
                          ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-400/20 hover:bg-yellow-400/30'
                          : 'text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/10 opacity-0 group-hover:opacity-100'
                      }`}
                      title={arquivo.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      aria-label={arquivo.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Star className={`w-3.5 h-3.5 ${arquivo.favorito ? 'fill-current' : ''}`} />
                    </button>
                  )}

                  {/* Botão Abrir */}
                  <button
                    onClick={() => onAbrirArquivo(arquivo)}
                    className="p-1 text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded transition-all"
                    title="Abrir arquivo no CorelDRAW"
                    aria-label="Abrir arquivo no CorelDRAW"
                  >
                    <PackageCheck className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Cards View (< 640px) - REDESIGN COMPACTO */}
        <div className="sm:hidden space-y-2">
          {arquivos.map((arquivo) => {
            return (
            <div
              key={arquivo.id}
              className="bg-navy-800/50 rounded-lg p-2.5 hover:bg-navy-700/50 transition-colors border border-navy-700/50"
            >
              {/* Linha 1: Nome do arquivo + Ações */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                  <span
                    className="text-sm font-medium text-white truncate"
                    title={arquivo.arquivoCaminho || arquivo.arquivoNome}
                  >
                    {arquivo.arquivoNome}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {arquivo.source !== 'cdr_save' && (
                    <button
                      onClick={() => onToggleFavorito(arquivo)}
                      className={`p-1 rounded transition-all ${
                        arquivo.favorito
                          ? 'text-yellow-400 bg-yellow-400/20'
                          : 'text-gray-500 hover:text-yellow-400 hover:bg-yellow-400/10'
                      }`}
                      title={arquivo.favorito ? 'Remover dos favoritos' : 'Favoritar'}
                    >
                      <Star className={`w-3 h-3 ${arquivo.favorito ? 'fill-current' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => onAbrirArquivo(arquivo)}
                    className="p-1 text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded transition-all"
                    title="Abrir no CorelDRAW"
                  >
                    <PackageCheck className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Linha 2: Categoria • Status */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {arquivo.prodCatItem && (
                  <>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${getCategoriaColor(arquivo.prodCatItem)}`}>
                      {CATEGORIAS[arquivo.prodCatItem]}
                    </span>
                    <span className="text-gray-600">•</span>
                  </>
                )}
                {arquivo.source === 'cdr_save' ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium text-purple-400 bg-purple-400/10" title={`O-${arquivo.orcId} / I-${arquivo.oitId}`}>
                    Orçamento
                  </span>
                ) : (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusColor(arquivo.status)}`}>
                    {getStatusText(arquivo.status)}
                  </span>
                )}
              </div>

              {/* Linha 3: Cliente • WhatsApp */}
              <div className="flex items-center gap-1.5 text-[11px] text-gray-300 mb-2">
                <span className="truncate">
                  {arquivo.cliente?.nome || 'Sem cliente'}
                </span>
                {arquivo.cliWhatsapp && (
                  <>
                    <span className="text-gray-600">•</span>
                    <span className="truncate text-gray-500">{arquivo.cliWhatsapp}</span>
                  </>
                )}
              </div>

              {/* Linha 4: Ver observações/detalhes (se tiver) */}
              {arquivo.observacoes && (
                <button
                  onClick={() => setArquivoDetalhes(arquivo)}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[10px] text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded transition-all"
                >
                  <MessageSquare className="w-3 h-3" />
                  <span>Ver observações</span>
                </button>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {arquivoDetalhes && (
        <ArquivoDetalhesModal
          arquivo={arquivoDetalhes}
          onClose={() => setArquivoDetalhes(null)}
          onAbrirArquivo={onAbrirArquivo}
        />
      )}
    </>
  );
}

export const ListaArquivos = memo(ListaArquivosComponent);
